import React from "react";

/**
 * SVG figures for the spatial reasoning items (IDs 25-30) of the IQ test.
 * Each figure clarifies the SETUP of the question but never reveals the
 * answer — the coachee still has to work out the transformation.
 *
 * Design principles:
 *   - Neutral zinc/slate palette with a single blue accent (#1E88E5)
 *     for direction indicators. Where a question explicitly names colors
 *     (item #26) we keep the natural red/blue.
 *   - viewBox is normalised so figures render crisp at any width.
 *   - No external assets — pure inline SVG so print & offline work.
 */
export default function IQFigure({ figureId }) {
  const Component = REGISTRY[figureId];
  if (!Component) return null;
  return (
    <div
      className="my-5 flex justify-center"
      data-testid={`iq-figure-${figureId}`}
    >
      <div className="max-w-xs w-full">
        <Component />
      </div>
    </div>
  );
}

// ---------- individual figures ----------

// #25 — triangle pointing up + circular arrow marked "180°"
function TriangleRotation180() {
  return (
    <svg viewBox="0 0 220 150" className="w-full h-auto">
      <polygon
        points="80,110 130,110 105,60"
        fill="none"
        stroke="#18181b"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {/* Rotation arrow — semi-circle above/right of the triangle */}
      <path
        d="M 155 65 A 30 30 0 1 1 155 105"
        fill="none"
        stroke="#1E88E5"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <polygon points="150,100 160,105 155,113" fill="#1E88E5" />
      <text x="175" y="90" fontFamily="ui-sans-serif" fontSize="13" fill="#1E88E5" fontWeight="600">
        180°
      </text>
    </svg>
  );
}

// #26 — square with red top-right + blue bottom-left corner cells
//        + clockwise rotation arrow
function SquareRotateCw90() {
  return (
    <svg viewBox="0 0 220 150" className="w-full h-auto">
      {/* Outer square */}
      <rect x="55" y="20" width="110" height="110" fill="white" stroke="#18181b" strokeWidth="2.5" />
      {/* Grid lines dividing into quadrants */}
      <line x1="110" y1="20" x2="110" y2="130" stroke="#a1a1aa" strokeWidth="1" />
      <line x1="55" y1="75" x2="165" y2="75" stroke="#a1a1aa" strokeWidth="1" />
      {/* Red cell top-right */}
      <rect x="110" y="20" width="55" height="55" fill="#E53935" fillOpacity="0.75" />
      {/* Blue cell bottom-left */}
      <rect x="55" y="75" width="55" height="55" fill="#1E88E5" fillOpacity="0.75" />
      {/* Clockwise rotation arrow */}
      <path
        d="M 190 40 A 22 22 0 1 1 190 110"
        fill="none"
        stroke="#18181b"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <polygon points="185,105 195,110 190,118" fill="#18181b" />
      <text x="180" y="30" fontFamily="ui-sans-serif" fontSize="11" fill="#18181b" fontWeight="600">
        90°
      </text>
    </svg>
  );
}

// #27 — plain L-shape (options describe the transformations)
function LShapeMirror() {
  return (
    <svg viewBox="0 0 220 150" className="w-full h-auto">
      <polygon
        points="80,30 110,30 110,90 150,90 150,120 80,120"
        fill="white"
        stroke="#18181b"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <text x="115" y="75" fontFamily="ui-sans-serif" fontSize="11" fill="#71717a">
        origineel
      </text>
    </svg>
  );
}

// #28 — square with diagonal LR ↗ + horizontal mirror axis (dashed)
function SquareDiagonalHflip() {
  return (
    <svg viewBox="0 0 220 150" className="w-full h-auto">
      <rect x="65" y="25" width="90" height="90" fill="white" stroke="#18181b" strokeWidth="2.5" />
      {/* Diagonal from bottom-left (65,115) to top-right (155,25) */}
      <line x1="65" y1="115" x2="155" y2="25" stroke="#1E88E5" strokeWidth="2.5" />
      {/* Horizontal mirror axis passing through vertical middle */}
      <line
        x1="35"
        y1="70"
        x2="185"
        y2="70"
        stroke="#71717a"
        strokeWidth="1.5"
        strokeDasharray="6 4"
      />
      <text x="35" y="62" fontFamily="ui-sans-serif" fontSize="11" fill="#71717a">
        spiegelas
      </text>
    </svg>
  );
}

// #29 — sequence: folded rectangle → triangle cut → "?" open
function PaperFoldCut() {
  return (
    <svg viewBox="0 0 300 130" className="w-full h-auto">
      {/* Panel 1: folded paper, fold line highlighted */}
      <g>
        <rect x="10" y="30" width="55" height="70" fill="white" stroke="#18181b" strokeWidth="2" />
        <line x1="65" y1="30" x2="65" y2="100" stroke="#1E88E5" strokeWidth="2.5" />
        <text x="10" y="22" fontFamily="ui-sans-serif" fontSize="10" fill="#71717a">
          gevouwen
        </text>
        <text x="70" y="70" fontFamily="ui-sans-serif" fontSize="9" fill="#1E88E5" fontWeight="600">
          vouw
        </text>
      </g>
      {/* Arrow */}
      <polygon points="100,65 115,60 115,70" fill="#a1a1aa" />
      <line x1="100" y1="65" x2="120" y2="65" stroke="#a1a1aa" strokeWidth="1.5" />
      {/* Panel 2: fold-side notched with a triangle cut */}
      <g>
        <path
          d="M 130 30 L 185 30 L 185 100 L 130 100 L 130 75 L 145 65 L 130 55 Z"
          fill="white"
          stroke="#18181b"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <line x1="130" y1="30" x2="130" y2="100" stroke="#1E88E5" strokeWidth="2.5" />
        <text x="130" y="22" fontFamily="ui-sans-serif" fontSize="10" fill="#71717a">
          uitgeknipt
        </text>
      </g>
      {/* Arrow */}
      <polygon points="215,65 230,60 230,70" fill="#a1a1aa" />
      <line x1="215" y1="65" x2="235" y2="65" stroke="#a1a1aa" strokeWidth="1.5" />
      {/* Panel 3: unfolded ? */}
      <g>
        <rect x="240" y="30" width="55" height="70" fill="white" stroke="#a1a1aa" strokeWidth="2" strokeDasharray="4 3" />
        <text x="260" y="75" fontFamily="ui-sans-serif" fontSize="28" fill="#a1a1aa" fontWeight="700">
          ?
        </text>
        <text x="240" y="22" fontFamily="ui-sans-serif" fontSize="10" fill="#71717a">
          uitgevouwen
        </text>
      </g>
    </svg>
  );
}

// #30 — isometric 3×3×3 cube with visible grid lines
function Cube3x3x3() {
  // Isometric projection: x-axis = +cos30, y-axis = -cos30, z-axis = up
  const s = 22; // sub-cube edge length
  const c30 = Math.cos(Math.PI / 6);
  const s30 = Math.sin(Math.PI / 6);
  const originX = 110;
  const originY = 130;
  const proj = (i, j, k) => [
    originX + (i - j) * s * c30,
    originY - k * s - (i + j) * s * s30,
  ];
  const lines = [];
  // Top face grid (k=3), lines along i and j
  for (let a = 0; a <= 3; a++) {
    const [x1, y1] = proj(a, 0, 3);
    const [x2, y2] = proj(a, 3, 3);
    lines.push(<line key={`t-i-${a}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#18181b" strokeWidth="1.2" />);
    const [x3, y3] = proj(0, a, 3);
    const [x4, y4] = proj(3, a, 3);
    lines.push(<line key={`t-j-${a}`} x1={x3} y1={y3} x2={x4} y2={y4} stroke="#18181b" strokeWidth="1.2" />);
  }
  // Right face grid (j=0), lines along i and k
  for (let a = 0; a <= 3; a++) {
    const [x1, y1] = proj(a, 0, 0);
    const [x2, y2] = proj(a, 0, 3);
    lines.push(<line key={`r-k-${a}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#18181b" strokeWidth="1.2" />);
    const [x3, y3] = proj(0, 0, a);
    const [x4, y4] = proj(3, 0, a);
    lines.push(<line key={`r-i-${a}`} x1={x3} y1={y3} x2={x4} y2={y4} stroke="#18181b" strokeWidth="1.2" />);
  }
  // Left face grid (i=0), lines along j and k
  for (let a = 0; a <= 3; a++) {
    const [x1, y1] = proj(0, a, 0);
    const [x2, y2] = proj(0, a, 3);
    lines.push(<line key={`l-k-${a}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#18181b" strokeWidth="1.2" />);
    const [x3, y3] = proj(0, 0, a);
    const [x4, y4] = proj(0, 3, a);
    lines.push(<line key={`l-j-${a}`} x1={x3} y1={y3} x2={x4} y2={y4} stroke="#18181b" strokeWidth="1.2" />);
  }
  // Face fills (very light) — top, left, right
  const topPts = [proj(0, 0, 3), proj(3, 0, 3), proj(3, 3, 3), proj(0, 3, 3)]
    .map((p) => p.join(",")).join(" ");
  const rightPts = [proj(0, 0, 0), proj(3, 0, 0), proj(3, 0, 3), proj(0, 0, 3)]
    .map((p) => p.join(",")).join(" ");
  const leftPts = [proj(0, 0, 0), proj(0, 3, 0), proj(0, 3, 3), proj(0, 0, 3)]
    .map((p) => p.join(",")).join(" ");
  return (
    <svg viewBox="0 0 220 160" className="w-full h-auto">
      <polygon points={topPts} fill="#f4f4f5" />
      <polygon points={rightPts} fill="#e4e4e7" />
      <polygon points={leftPts} fill="#fafafa" />
      {lines}
    </svg>
  );
}

const REGISTRY = {
  triangle_rotation_180: TriangleRotation180,
  square_rotate_cw_90: SquareRotateCw90,
  l_shape_mirror: LShapeMirror,
  square_diagonal_hflip: SquareDiagonalHflip,
  paper_fold_cut: PaperFoldCut,
  cube_3x3x3: Cube3x3x3,
};
